 import moment from 'moment';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import Alert from '@mui/material/Alert';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { MAX_WITH_MOBILE_MEDIA } from '../../config/getTheme';

const TimeRangeInput  = props => {
  return (
    <Stack key={props.key} direction='row' spacing={1}>
      <Box style={{minWidth: '100px'}}>
        <TimeField
          label={null}
          placeholder='9:00am'
          value={props.start_time ? moment(props.start_time, 'hh:mm a') : null}
          onChange={momentObj => props.onStartChange(momentObj)}
          error={props.error}
          sx={{
            '& .MuiInputBase-input': {
              [MAX_WITH_MOBILE_MEDIA]: {
                paddingLeft: '8px',
                paddingRight: '8px',
              },
            }
          }}
        />
      </Box>
      <Box style={{paddingTop: '0.8em', color: 'rgba(0,0,0,0.4)'}}>
        -
      </Box>
      <Box style={{minWidth: '100px'}}>
        <TimeField
          label={null}
          placeholder='5:00pm'
          value={props.end_time ? moment(props.end_time, 'hh:mm a') : null}
          onChange={momentObj => props.onEndChange(momentObj)}
          error={props.error}
          sx={{
            '& .MuiInputBase-input': {
              [MAX_WITH_MOBILE_MEDIA]: {
                paddingLeft: '8px',
                paddingRight: '8px',
              },
            }
          }}
        />
      </Box>
      {props.onDelete
        ? <Button onClick={() => props.onDelete()} sx={{minWidth: 'auto', marginLeft: '0 !important'}}>
          <DeleteIcon />
        </Button>
        : <></>
      }
    </Stack>
  );
};

const handleTimeRangeDelete = ({setTimes, times, onChange}) => index => e => {
  const newTimes = times.filter((t,i) => i !== index);
  setTimes(newTimes);
  onChange(newTimes);
};

const handleTimeRangeChange = ({setTimes, onChange, times, isStart}) => (
  index => momentObj => {
    const timeStr = momentObj.format('hh:mm a');
    const newTimes = (
      (!times[index] || !times.length)
      ? [
        ...times,
        {
          start_time: isStart ? timeStr : null,
          end_time: isStart ? null : timeStr,
        }
      ]
      : times.map((t, i) => (
        i === index
        ? {
          start_time: isStart ? timeStr : t.start_time,
          end_time: isStart ? t.end_time : timeStr,
        }
        : t
      ))
    );
    setTimes(newTimes);
    onChange(newTimes);
  }
);

const handleAdd = ({times, setTimes, onChange, newTime, setNewTime}) => () => {
  if (!newTime) return;
  const startStr = moment(newTime.start_time, 'hh:mm a').format('hh:mm a');
  const endStr = moment(newTime.end_time, 'hh:mm a').format('hh:mm a');
  if (startStr === 'Invalid date' || endStr === 'InvalidDate') return;
  const newTimes = [...times, newTime];
  setTimes(newTimes);
  setNewTime({start_time: null, end_time: null});
  onChange(newTimes);
};

const handleNewTimeChange = ({
  newTime,
  setNewTime,
  isStart
}) => momentObj => {
  const priorState = newTime || {start_time: null, end_time: null};
  const timeStr = momentObj.format('hh:mm a');
  const newState = (
    isStart
    ? {...newTime, start_time: timeStr}
    : {...newTime, end_time: timeStr}
  );
  setNewTime(newState);
};

const validateAvailabilities = ({start_time, end_time, times}) => {
  // start time and end time must be present
  if (!start_time || !end_time) return true;
  // end time must be after start time
  const start = moment(start_time, 'hh:mm a');
  const end = moment(end_time, 'hh:mm a');
  const isValid = end.isAfter(start);
  if (!isValid) return false;
  // start_time must be after end_time of other times
  const otherTimes = times.filter(t => t.start_time !== start_time);
  const hasOverlap = times.reduce((acc, t) => {
    if (
      start.isAfter(moment(t.start_time, 'hh:mm a'))
      && start.isBefore(moment(t.end_time, 'hh:mm a'))
    ) return true;
    if (
      end.isAfter(moment(t.start_time, 'hh:mm a'))
      && end.isBefore(moment(t.end_time, 'hh:mm a'))
    ) return true;
    return acc;
  }, false);
  return !hasOverlap;
};

const DayAvailability = props => {
  const {onChange} = props;
  const [times, setTimes] = useState(props.defaultTimes);
  const [newTime, setNewTime] = useState({start_time: null, end_time: null});
  const onAdd = handleAdd({times, setTimes, onChange, newTime, setNewTime});
  const onStartChange = handleTimeRangeChange({
    times,
    setTimes,
    onChange,
    isStart: true,
  });
  const onEndChange = handleTimeRangeChange({
    times,
    setTimes,
    onChange,
    isStart: false,
  });
  const onDelete = handleTimeRangeDelete({times, setTimes, onChange});
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box key={`${props.day}-availability`}>
        <Stack direction='row' spacing={1}>
          <Box style={{paddingTop: '0.5rem'}}>
            <Typography
              variant='body1'
              style={{fontWeight: '700', minWidth: '42px'}}
            >
              {props.day}
            </Typography>
          </Box>
          <Stack spacing={2}>
            {(times || []).map(({start_time, end_time}, i) =>
              <TimeRangeInput
                key={`${props.day}-${i}`}
                start_time={start_time}
                end_time={end_time}
                onStartChange={onStartChange(i)}
                onEndChange={onEndChange(i)}
                onDelete={onDelete(i)}
                error={!validateAvailabilities({start_time, end_time, times})}
              />
            )}
            <Stack direction='row' spacing={1}>
              {
                times
                ? <TimeRangeInput
                  start_time={newTime ? newTime.start_time : null}
                  end_time={newTime ? newTime.end_time : null}
                  onStartChange={handleNewTimeChange({
                    newTime,
                    setNewTime,
                    isStart: true
                  })}
                  onEndChange={handleNewTimeChange({newTime, setNewTime})}
                  onDelete={null}
                  error={!validateAvailabilities({
                    start_time: newTime.start_time,
                    end_time: newTime.end_time,
                    times
                  })}
                />
                : null
              }
              <Button onClick={onAdd} sx={{minWidth: 'auto', marginLeft: '0 !important'}}>
                <SaveIcon />
              </Button>
            </Stack>
            {newTime && newTime.start_time
              ? <Stack direction='row'>
                <Alert severity='warning'>
                  You have unsaved changes! Please click the save button when you're done editing.
                </Alert>
              </Stack>
              : null
            }
          </Stack>
        </Stack>
        <Divider style={{marginTop: '1em'}}/>
      </Box>
    </LocalizationProvider>
  );
};

DayAvailability.propTypes = {
  day: PropTypes.string,
  defaultTimes: PropTypes.arrayOf(PropTypes.shape({
    start_time: PropTypes.string, // hh:mm a (e.g. 09:00 AM)
    end_time: PropTypes.string, // hh:mm a (e.g. 05:00 PM)
  })),
  onChange: PropTypes.func, // ([{start_time, end_time}]) => {...}
}

export default DayAvailability;
